import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Skeleton,
  Typography,
} from "antd";
import { Option } from "antd/lib/mentions";
import GoBack from "commons/GoBack";
import FormDatePicker from "components/common/FormDatePicker";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { GetEmployeeDetails, UpdateEmployeeProfile } from "utility/action";
import {
  cityPattern,
  emailPattern,
  formatZipCode,
  servicesNamePattern,
  streetAddressPattern,
  tenDigit,
  zipPattern,
} from "utility/constant";
import SuccessModel from "utility/SuccessModel";

type initialValues = {
  email: string;
  firstName: string;
  lastName: string;
  phone: any;
  dob: moment.Moment | null;
  streetAdress: string;
  city: string;
  state: string;
  zip: any;
  insuranceCarrier: string;
  gender: string;
  role: string;
  note?: string;
};

export const EmployeeEditDetails = () => {
  //   const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const employeeId = id;
  const [searchParams] = useSearchParams();
  const employerProfileId = searchParams.get("employerId");
  const [memberForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [successModel, setSuccessModel] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [skeletonLoader, setSkeletonLoader] = useState(true);
  const [employeeProfile, setEmployeProfile] = useState<initialValues>();
  const [employeeData, setEmployeeData] = useState<any>({});
  const [employeeDetails, setEmployeeDetails] = useState<Boolean>(false);
  const [employerId, setEmployerId] = useState("");

  const handleValuesChange = () => {
    setSaveDisabled(false);
  };

  const fetchEmployeeData = useCallback(async () => {
    try {
      setSkeletonLoader(true);
      await GetEmployeeDetails(employeeId)
        .then((response) => {
          setEmployeProfile(response?.profile);
          setEmployeeData(response);
        })
        .catch((error) => {
          console.log("error", error);
          setSkeletonLoader(false);
        });
      setEmployerId(employerProfileId!);
      setSkeletonLoader(false);
    } catch (error) {
      setSkeletonLoader(false);
      console.log(error);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployeeData();
    setSuccessModel(false);
  }, [fetchEmployeeData]);

  const updateEmployee = async (values: initialValues | any) => {
    try {
      setLoading(true);
      const inputData = Object.assign({
        email: values?.email,
        firstName: values?.firstName !== "" ? values?.firstName : undefined,
        gender: values?.gender !== "" ? values?.gender : undefined,
        lastName: values?.lastName !== "" ? values?.lastName : undefined,
        dob: values?.dob ? moment(values?.dob).format("YYYY-MM-DD") : undefined,
        phone: values?.phone !== "" ? values?.phone : undefined,
        note: values?.note || "",
        streetAdress:
          values?.streetAdress !== "" ? values?.streetAdress : undefined,
        city: values?.city !== "" ? values?.city : undefined,
        state: values?.state !== "" ? values?.state : undefined,
        zip: values?.zip !== "" ? values?.zip : undefined,
        insuranceCarrier:
          values?.insuranceCarrier !== ""
            ? values?.insuranceCarrier
            : undefined,
      });
      await UpdateEmployeeProfile(inputData);

      setLoading(false);
      setEmployeeDetails(true);
      fetchEmployeeData();
      setTimeout(() => {
        setEmployeeDetails(false);
      }, 3000);
    } catch (error) {
      setLoading(false);
      console.log(error);
      messageApi.open({
        type: "error",
        content: "Error.",
      });
    }
  };

  const initialValues = {
    email: employeeProfile?.email
      ? employeeProfile?.email
      : employeeData?.emailID,
    firstName: employeeProfile?.firstName
      ? employeeProfile?.firstName
      : employeeData?.firstName,
    lastName: employeeProfile?.lastName
      ? employeeProfile?.lastName
      : employeeData?.lastName,
    phone: employeeProfile?.phone,
    dob: employeeProfile?.dob
      ? moment(employeeProfile?.dob, "YYYY-MM-DD")
      : null,
    streetAdress: employeeProfile?.streetAdress,
    city: employeeProfile?.city,
    note: employeeProfile?.note,
    state: employeeProfile?.state,
    zip: formatZipCode(employeeProfile?.zip),
    insuranceCarrier: employeeProfile?.insuranceCarrier,
    gender: employeeProfile?.gender,
    role: employeeData?.epcEmployerID === "EMPLOYEE",
  };

  const renderSkeletonForm = () => (
    <Row gutter={20}>
      {Array.from({ length: 11 }).map((_, i) => (
        <Col span={i === 10 ? 24 : 12} key={i}>
          <Skeleton.Input
            active
            block
            style={{ height: 40, marginBottom: 24 }}
          />
        </Col>
      ))}
      <Col span={24} style={{ textAlign: "center" }}>
        <Skeleton.Button active size="large" style={{ width: 100 }} />
      </Col>
    </Row>
  );

  return (
    <>
      {contextHolder}
      <Row justify="space-between" align="middle">
        <GoBack
          backPath={
            employerProfileId
              ? `/dpc/employer-profile/${employerId}`
              : `/dpc/employer-profile/${employerProfileId}`
          }
        />
        <Typography.Title level={4} className="text-center mb-0">
          {skeletonLoader ? (
            <Skeleton.Input
              active
              size="small"
              style={{
                width: 200,
                height: 24,
                display: "inline-block",
              }}
            />
          ) : employeeData?.profile?.firstName ? (
            `${employeeData?.profile?.firstName || ""} ${
              employeeData?.profile?.lastName || ""
            }`
          ) : (
            ""
          )}
        </Typography.Title>
        <Button style={{ marginLeft: 60 }} />
      </Row>
      <Divider style={{ margin: "8px 0" }} />
      <Row justify="center" className="update-form">
        <Col span={16} md={16} xs={24} sm={24}>
          {skeletonLoader ? (
            renderSkeletonForm()
          ) : (
            <Form
              form={memberForm}
              initialValues={initialValues}
              autoComplete="off"
              onFinish={updateEmployee}
              layout="vertical"
              className="mt-20"
              onValuesChange={handleValuesChange}
            >
              <Row gutter={20}>
                <Col span={12}>
                  <Form.Item
                    label="First name"
                    name="firstName"
                    rules={[
                      {
                        pattern: servicesNamePattern,
                        message: "Please enter a valid first name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Last name"
                    name="lastName"
                    rules={[
                      {
                        pattern: servicesNamePattern,
                        message: "Please enter a valid last name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      {
                        pattern: emailPattern,
                        message: "Please enter a valid email",
                      },
                    ]}
                  >
                    <Input disabled={true} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Phone"
                    name="phone"
                    rules={[
                      {
                        pattern: tenDigit,
                        message: "Please enter a valid phone number",
                      },
                    ]}
                  >
                    <InputNumber
                      controls={false}
                      parser={(value: any) => {
                        const cleanedValue = value.replace(/\D/g, "");
                        return cleanedValue.slice(0, 10);
                      }}
                      style={{ width: "100%", borderRadius: "5px" }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12} md={12} xs={24} sm={24}>
                  {/* @ts-ignore */}
                  <FormDatePicker name="dob" label="Date of Birth" />
                </Col>
                <Col span={12}>
                  <Form.Item label="Gender" name="gender">
                    <Select
                      placeholder="Select Gender"
                      allowClear
                      getPopupContainer={(trigger) => trigger.parentNode}
                    >
                      <Option value="MALE">Male</Option>
                      <Option value="FEMALE">Female</Option>
                      <Option value="OTHER">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Street address"
                    name="streetAdress"
                    rules={[
                      {
                        pattern: streetAddressPattern,
                        message: "Please enter a valid street address",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="City"
                    name="city"
                    rules={[
                      {
                        pattern: cityPattern,
                        message: "Please enter a valid city name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="State"
                    name="state"
                    rules={[
                      {
                        pattern: cityPattern,
                        message: "Please enter a valid state name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Zip code"
                    name="zip"
                    rules={[
                      {
                        pattern: zipPattern,
                        message: "Please enter a valid ZIP code",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Insurance carrier"
                    name="insuranceCarrier"
                    rules={[
                      {
                        pattern: /^[a-zA-Z0-9 ]+$/,
                        message: "Please enter a valid insurance carrier",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={24} md={24} xs={24} sm={24}>
                  <Form.Item label="Note" name="note">
                    <Input.TextArea
                      rows={3}
                      placeholder="Write any notes here..."
                    />
                  </Form.Item>
                </Col>
                <Col span={24} style={{ textAlign: "center" }}>
                  <Button
                    size="large"
                    className="theme-btn"
                    htmlType="submit"
                    loading={loading}
                    disabled={saveDisabled}
                  >
                    Save
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </Col>
      </Row>
      <SuccessModel
        message="Employee Details Updated Successfully!"
        open={employeeDetails}
      />
    </>
  );
};
